import { useState, useEffect, useMemo } from "react";
import { Plus, Trash2, Loader2, AlertTriangle, Building2 } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYGCompact } from "../../utils/format";
import { toast } from "sonner";

// Fix P (2026-08-31): pantalla nueva — hasta ahora finance_checkpoint_b.py
// exponía alta/listado/baja de gastos administrativos mensuales pero no
// existía ninguna pantalla para cargarlos, así que la planilla de "gastos
// administrativos" de las tres por obra daba cero siempre.
const MARCAS_EDIT_ROLES = ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador"];

function mesActual() {
  return new Date().toISOString().slice(0, 7);
}

export default function GastosAdminTab() {
  const { user } = useAuth();
  const puedeEditar = MARCAS_EDIT_ROLES.includes(user?.role);

  const [mes, setMes] = useState(mesActual());
  const [imputadoFiltro, setImputadoFiltro] = useState("");
  const [gastos, setGastos] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ concepto: "", monto: "", imputado_a: "empresa", obra_nombre: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { mes };
      if (imputadoFiltro) params.imputado_a = imputadoFiltro;
      const { data } = await axios.get(`${API}/finance/gastos-admin-mensuales`, { params, withCredentials: true });
      setGastos(data);
    } catch { toast.error("Error al cargar gastos administrativos"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/obras`, { params: { status: "activa" }, withCredentials: true })
      .then(({ data }) => setObras(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [mes, imputadoFiltro]);

  const total = useMemo(() => gastos.reduce((s, g) => s + (g.monto || 0), 0), [gastos]);

  const handleImputadoChange = (val) => {
    const obra = obras.find(o => o.id === val);
    setForm(f => ({ ...f, imputado_a: val, obra_nombre: obra ? obra.nombre : "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.concepto || !form.monto) { toast.error("Complete concepto y monto"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/finance/gastos-admin-mensuales`, {
        mes, concepto: form.concepto, monto: Number(form.monto),
        imputado_a: form.imputado_a, obra_nombre: form.obra_nombre,
      }, { withCredentials: true });
      toast.success("Gasto administrativo cargado");
      setForm({ concepto: "", monto: "", imputado_a: "empresa", obra_nombre: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar el gasto"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Borrar este gasto administrativo?")) return;
    try {
      await axios.delete(`${API}/finance/gastos-admin-mensuales/${id}`, { withCredentials: true });
      toast.success("Gasto eliminado");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-5" data-testid="gastos-admin-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Período</label>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
            data-testid="gastos-admin-mes-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Imputado a</label>
          <select value={imputadoFiltro} onChange={e => setImputadoFiltro(e.target.value)}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
            data-testid="gastos-admin-filtro-select">
            <option value="">Todos</option>
            <option value="empresa">Toda la empresa</option>
            {obras.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
          </select>
        </div>
      </div>

      {puedeEditar && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="gastos-admin-form">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Concepto *</label>
            <input value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))}
              placeholder="Ej. Alquiler oficina" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              data-testid="gastos-admin-concepto-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Monto (Gs) *</label>
            <input type="number" value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))}
              placeholder="0" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
              data-testid="gastos-admin-monto-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Imputado a</label>
            <select value={form.imputado_a} onChange={e => handleImputadoChange(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white"
              data-testid="gastos-admin-imputado-select">
              <option value="empresa">Toda la empresa</option>
              {obras.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving}
            className="bg-[#2E6DA4] hover:bg-[#255a89] text-white rounded-xl py-2 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="gastos-admin-guardar-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            Cargar
          </button>
        </form>
      )}

      {!loading && gastos.length === 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="gastos-admin-empty-warning">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>No hay gastos administrativos cargados para este período{imputadoFiltro ? " y esta imputación" : ""}. Mientras no se carguen, la planilla de gastos administrativos de las obras va a dar cero.</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Mes</th>
              <th className="px-4 py-3 text-left">Concepto</th>
              <th className="px-4 py-3 text-left">Imputación</th>
              <th className="px-4 py-3 text-right">Monto</th>
              {puedeEditar && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Cargando...</td></tr>
            ) : gastos.map(g => (
              <tr key={g.id} data-testid="gastos-admin-row">
                <td className="px-4 py-3 text-gray-600">{g.mes}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{g.concepto}</td>
                <td className="px-4 py-3 text-gray-600 flex items-center gap-1.5">
                  <Building2 size={12} className="text-gray-400" />
                  {g.imputado_a === "empresa" ? "Toda la empresa" : (g.obra_nombre || g.imputado_a)}
                </td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatPYGCompact(g.monto)}</td>
                {puedeEditar && (
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(g.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`gastos-admin-delete-${g.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {!loading && gastos.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right font-semibold text-gray-700">Total del período</td>
                <td className="px-4 py-3 text-right font-bold tabular-nums text-[#2E6DA4]" data-testid="gastos-admin-total">{formatPYGCompact(total)}</td>
                {puedeEditar && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
